import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of, BehaviorSubject } from 'rxjs';
import { environment } from '@env/environment';
import { AuthResult, LoginRequest, RegisterRequest, User } from '../models/user.model';

export interface RegisterResult {
  message: string;
  email: string;
  requiresEmailConfirmation: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/auth`;
  private readonly USER_KEY = 'wwhxp_user';
  private readonly TOKEN_KEY = 'wwhxp_token';

  private userSignal = signal<User | null>(null);
  private tokenSignal = signal<string | null>(null);

  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.userSignal());
  readonly isAdmin = computed(() => this.userSignal()?.role === 'admin');

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadStoredAuth();
  }

  private loadStoredAuth(): void {
    const storedUser = localStorage.getItem(this.USER_KEY);
    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    
    if (storedUser && storedToken) {
      try {
        this.userSignal.set(JSON.parse(storedUser));
        this.tokenSignal.set(storedToken);
      } catch {
        this.clearAuth();
      }
    }
  }

  getToken(): string | null {
    return this.tokenSignal();
  }

  login(request: LoginRequest): Observable<AuthResult> {
    return this.http.post<AuthResult>(`${this.API_URL}/login`, request, { withCredentials: true })
      .pipe(
        tap(result => this.handleAuthResult(result))
      );
  }

  register(request: RegisterRequest): Observable<RegisterResult> {
    return this.http.post<RegisterResult>(`${this.API_URL}/register`, request, { withCredentials: true });
  }

  confirmEmail(token: string): Observable<any> {
    return this.http.get(`${this.API_URL}/confirm-email`, { params: { token } });
  }

  resendConfirmationEmail(email: string): Observable<any> {
    return this.http.post(`${this.API_URL}/resend-confirmation`, { email });
  }

  refreshToken(): Observable<AuthResult | null> {
    return this.http.post<AuthResult>(`${this.API_URL}/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(result => this.handleAuthResult(result)),
        catchError(() => {
          this.logout();
          return of(null);
        })
      );
  }

  logout(): void {
    this.clearAuth();
    this.http.post(`${this.API_URL}/revoke`, {}, { withCredentials: true })
      .subscribe({
        complete: () => {
          this.router.navigate(['/login']);
        },
        error: () => {
          this.router.navigate(['/login']);
        }
      });
  }

  handleAuthResult(result: AuthResult): void {
    const user: User = {
      id: result.userId,
      email: result.email,
      firstName: result.firstName,
      lastName: result.lastName,
      role: result.role as 'user' | 'admin',
      isActive: true,
      createdAt: new Date().toISOString(),
      hasHomeXpertLink: false,
      hasBillingAddress: false
    };

    this.userSignal.set(user);
    this.tokenSignal.set(result.accessToken);

    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    localStorage.setItem(this.TOKEN_KEY, result.accessToken);
  }

  private clearAuth(): void {
    this.userSignal.set(null);
    this.tokenSignal.set(null);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_KEY);
  }
}
